import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Cookies from "js-cookie";
import { createEcho } from "../utils/echo";
import { useGetCompanyInfoQuery } from "../redux/services/settingsApi";
import { baseApi } from "../redux/services/baseApi";
import { useAppDispatch } from "../redux/services/store";
import { FLEETRIFYROUTEPATH } from "../routes/routesPath";
import { playNotificationSound } from "../utils/notificationSound";
import { handleDispatchOrderEvent } from "./useDispatchOrderAlarm";

const showBrowserNotification = (title: string, body: string) => {
  if (!("Notification" in window)) return;

  if (Notification.permission === "granted") {
    new Notification(title, { body, icon: "/logo.png" });
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission();
  }
};

const humanizeOrderStatus = (status?: string) => {
  if (!status) return "updated";
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export const useOrderNotifications = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAuthenticated = !!Cookies.get("token");
  const { data: companyInfoData } = useGetCompanyInfoQuery(undefined, {
    skip: !isAuthenticated,
  });
  const companyId = companyInfoData?.data?.companyInfo?.id;

  // Ask for notification permission on the user's first interaction with the
  // page, rather than waiting for the first order to arrive — by the time an
  // order actually comes in, the browser is already allowed to show it.
  // Safari in particular only honors requestPermission() when it's called
  // directly from a genuine user gesture (click/tap/keypress) — calling it
  // from a plain effect on login, like this used to, is silently ignored
  // there, so browser notifications would never work in Safari at all.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!("Notification" in window)) return;

    const requestOnGesture = () => {
      if (Notification.permission === "default") {
        Notification.requestPermission();
      }
    };

    window.addEventListener("pointerdown", requestOnGesture);
    window.addEventListener("keydown", requestOnGesture);

    return () => {
      window.removeEventListener("pointerdown", requestOnGesture);
      window.removeEventListener("keydown", requestOnGesture);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!isAuthenticated || !token || !companyId) return;

    const echo = createEcho(token);
    const channelName = `company.${companyId}.orders`;
    const channel = echo.private(channelName);

    // Dev-only diagnostics — makes it possible to tell, from the console,
    // whether the socket connected, whether the private channel actually
    // authorized, and whether ANY event is arriving on it at all. None of
    // this runs in production.
    if (process.env.NODE_ENV !== "production") {
      const pusherConnection = (echo as any)?.connector?.pusher?.connection;
      pusherConnection?.bind("state_change", (states: { previous: string; current: string }) => {
        console.debug(`[useOrderNotifications] Pusher connection: ${states.previous} → ${states.current}`);
      });
      pusherConnection?.bind("error", (err: any) => {
        console.error("[useOrderNotifications] Pusher connection error:", err);
      });

      channel.subscribed(() => {
        console.debug(`[useOrderNotifications] Subscribed to "${channelName}"`);
      });
      channel.error((err: any) => {
        console.error(`[useOrderNotifications] Failed to subscribe to "${channelName}" — check the token/company id, and that /broadcasting/auth authorizes it:`, err);
      });
    }

    const handleOrderCreated = (event: any) => {
      // Covers every order type — day-to-day dispatch orders AND relocation
      // requests (local + interstate) all fire the same alert.
      const isRelocation = event.order_type === "local_relocation" || event.order_type === "interstate_relocation";
      const isDayToDay = event.order_type === "local_dispatch";

      const title = isRelocation
        ? "New Relocation Request"
        : isDayToDay
        ? "New Day to Day Delivery order"
        : "New order";

      const viewPath = isDayToDay
        ? FLEETRIFYROUTEPATH.VIEW_ORDER.replace(":orderId", String(event.id))
        : isRelocation
        ? FLEETRIFYROUTEPATH.RELOCATION_QUOTES
        : FLEETRIFYROUTEPATH.ORDERS;

      playNotificationSound();

      toast(`${title} — ${event.customer?.name ?? "Customer"}`, {
        description: `${event.pickup_location ?? ""} → ${event.delivery_location ?? ""}`,
        action: {
          label: "View",
          onClick: () => navigate(viewPath),
        },
      });

      showBrowserNotification(
        title,
        event.customer?.name
          ? `${event.customer.name} — ${event.pickup_location} → ${event.delivery_location}`
          : `A new ${isRelocation ? "relocation request" : "order"} has arrived`
      );

      dispatch(baseApi.util.invalidateTags(["staffNotifications", "Orders", "RelocationOrders"]));
    };

    // Fired whenever a driver (or the system) moves an order to a new
    // status — e.g. accepted, picked up, delivered.
    const handleOrderStatusUpdated = (event: any) => {
      const orderId = event?.id ?? event?.order_id ?? event?.latestUpdate?.id;
      const status = event?.status ?? event?.latestUpdate?.status;
      const label = event?.latestUpdate?.label ?? humanizeOrderStatus(status);
      const orderLabel = event?.order_number ? `#${event.order_number}` : orderId ? `#${orderId}` : "an order";

      playNotificationSound();

      toast(`Order ${orderLabel} — ${label}`, {
        description: event?.latestUpdate?.note ?? event?.note ?? undefined,
        action: orderId
          ? {
              label: "View",
              onClick: () => navigate(FLEETRIFYROUTEPATH.VIEW_ORDER.replace(":orderId", String(orderId))),
            }
          : undefined,
      });

      showBrowserNotification(`Order ${orderLabel} — ${label}`, event?.latestUpdate?.note ?? event?.note ?? "Order status updated");

      dispatch(baseApi.util.invalidateTags(["staffNotifications", "Orders", "RelocationOrders"]));

      // A customer accepting the company's counter, or paying, rings for a minute.
      if (orderId) {
        handleDispatchOrderEvent(dispatch, String(orderId), { status, payment_status: event?.payment_status, order_type: event?.order_type }, () =>
          navigate(FLEETRIFYROUTEPATH.VIEW_ORDER.replace(":orderId", String(orderId)))
        );
      }
    };

    channel.listen(".order.created", handleOrderCreated);
    channel.listen(".order.status.updated", handleOrderStatusUpdated);

    return () => {
      channel.stopListening(".order.created");
      channel.stopListening(".order.status.updated");
    };
  }, [isAuthenticated, companyId, dispatch, navigate]);
};
